import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Sparkles, Check, X, Brain, ArrowLeft, Filter } from 'lucide-react'
import { toast } from 'sonner'
import { Link } from 'react-router-dom'
import { reconciliationApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { ConfidenceBadge } from '@/components/reconciliation'
import type { RuleSuggestion, RuleSuggestionStatus } from '@/types'
import { useState } from 'react'

export default function RuleSuggestionsPage() {
  const queryClient = useQueryClient()
  const [statusFilter, setStatusFilter] = useState<RuleSuggestionStatus | 'ALL'>('PENDING')

  const { data: suggestionsData, isLoading } = useQuery({
    queryKey: ['rule-suggestions', statusFilter],
    queryFn: () => reconciliationApi.ruleSuggestions.list(
      statusFilter !== 'ALL' ? statusFilter as RuleSuggestionStatus : undefined
    ),
  })

  const acceptMutation = useMutation({
    mutationFn: (id: string) => reconciliationApi.ruleSuggestions.accept(id),
    onSuccess: (rule) => {
      queryClient.invalidateQueries({ queryKey: ['rule-suggestions'] })
      queryClient.invalidateQueries({ queryKey: ['rule-suggestions-count'] })
      queryClient.invalidateQueries({ queryKey: ['reconciliation-rules'] })
      toast.success(`Rule "${rule.rule_name}" created successfully!`)
    },
    onError: () => toast.error('Failed to create rule'),
  })

  const dismissMutation = useMutation({
    mutationFn: (id: string) => reconciliationApi.ruleSuggestions.dismiss(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rule-suggestions'] })
      queryClient.invalidateQueries({ queryKey: ['rule-suggestions-count'] })
      toast.success('Suggestion dismissed')
    },
    onError: () => toast.error('Failed to dismiss suggestion'),
  })

  const suggestions = suggestionsData?.suggestions || []
  const pendingCount = suggestions.filter(s => s.status === 'PENDING').length

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/reconciliation/rules">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <div className="flex items-center gap-2">
              <Brain className="h-6 w-6 text-primary" />
              <h1 className="text-2xl font-bold">AI Rule Suggestions</h1>
              {pendingCount > 0 && (
                <Badge variant="secondary">{pendingCount} pending</Badge>
              )}
            </div>
            <p className="text-muted-foreground">
              Review and accept AI-generated rules to automate future categorization
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as RuleSuggestionStatus | 'ALL')}>
            <SelectTrigger className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="PENDING">Pending</SelectItem>
              <SelectItem value="ACCEPTED">Accepted</SelectItem>
              <SelectItem value="DISMISSED">Dismissed</SelectItem>
              <SelectItem value="ALL">All</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {isLoading ? (
        <div className="grid gap-4">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <Skeleton className="h-6 w-1/3 mb-2" />
                <Skeleton className="h-4 w-2/3 mb-4" />
                <Skeleton className="h-8 w-40" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : suggestions.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Sparkles className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No suggestions found</h3>
            <p className="text-muted-foreground">
              {statusFilter === 'PENDING'
                ? 'AI will suggest rules as you process more transactions.'
                : 'No suggestions match the current filter.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {suggestions.map((suggestion) => (
            <SuggestionCard
              key={suggestion.suggestion_id}
              suggestion={suggestion}
              onAccept={() => acceptMutation.mutate(suggestion.suggestion_id)}
              onDismiss={() => dismissMutation.mutate(suggestion.suggestion_id)}
              isAccepting={acceptMutation.isPending}
              isDismissing={dismissMutation.isPending}
            />
          ))}
        </div>
      )}
    </div>
  )
}

interface SuggestionCardProps {
  suggestion: RuleSuggestion
  onAccept: () => void
  onDismiss: () => void
  isAccepting: boolean
  isDismissing: boolean
}

function SuggestionCard({ suggestion, onAccept, onDismiss, isAccepting, isDismissing }: SuggestionCardProps) {
  const isPending = suggestion.status === 'PENDING'

  return (
    <Card className={!isPending ? 'opacity-60' : ''}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-primary/10">
              <Sparkles className="h-4 w-4 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg">{suggestion.suggested_name}</CardTitle>
              <CardDescription className="mt-1">
                {suggestion.ai_reasoning || 'AI-generated rule suggestion'}
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ConfidenceBadge score={suggestion.confidence_score} />
            <StatusBadge status={suggestion.status} />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Conditions Preview */}
        <div className="mb-4">
          <p className="text-sm font-medium mb-2">Conditions:</p>
          <div className="flex flex-wrap gap-2">
            {suggestion.suggested_conditions?.map((condition, idx) => (
              <Badge key={idx} variant="outline" className="font-mono text-xs">
                {condition.field} {condition.operator} "{String(condition.value)}"
              </Badge>
            ))}
          </div>
        </div>
        {/* Actions Preview */}
        <div className="mb-4">
          <p className="text-sm font-medium mb-2">Actions:</p>
          <div className="flex flex-wrap gap-2">
            {suggestion.suggested_actions?.map((action, idx) => (
              <Badge key={idx} variant="secondary" className="text-xs">
                {action.action_type}: {String(action.value)}
              </Badge>
            ))}
          </div>
        </div>
        {/* Action Buttons */}
        {isPending && (
          <div className="flex items-center gap-2 pt-2 border-t">
            <Button onClick={onAccept} disabled={isAccepting || isDismissing}>
              <Check className="h-4 w-4 mr-1" />
              Accept & Create Rule
            </Button>
            <Button variant="outline" onClick={onDismiss} disabled={isAccepting || isDismissing}>
              <X className="h-4 w-4 mr-1" />
              Dismiss
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function StatusBadge({ status }: { status: RuleSuggestionStatus }) {
  const config = {
    PENDING: { label: 'Pending', variant: 'outline' as const },
    ACCEPTED: { label: 'Accepted', variant: 'default' as const },
    DISMISSED: { label: 'Dismissed', variant: 'secondary' as const },
  }
  const { label, variant } = config[status]
  return <Badge variant={variant}>{label}</Badge>
}

